<template>
  <div class="flex flex-col gap-y-2 items-center">
    <div>
      <Label v-if="fieldName">{{ fieldName }}</Label>
      <p>{{ description }}</p>
    </div>
    <p v-if="fileName" class="font-mono">{{ fileName }}</p>
    <input
      v-if="!fileSrc"
      type="file"
      :name="name ?? 'input_file'"
      :id="id"
      class="hidden w-3/4 appearance-none rounded border px-3 py-2 leading-tight text-gray-700 shadow outline-none hover:border-gray-500 focus:ring-2 focus:ring-secondary"
      accept=""
      @change="setFile"
    />
    <label
      v-if="!fileSrc"
      :for="id"
      class="cursor-pointer bg-secondary hover:bg-primary text-white inline-flex items-center justify-center gap-x-2 rounded px-4 py-1 font-medium focus:outline-none"
      >Choose file</label
    >
    <p class="py-2 text-link-red">{{ fileError }}</p>
    <div class="mb-2 flex gap-x-2" v-if="fileSrc">
      <Button @click="downloadFile" icon="file-download"> Download </Button>
      <Button @click="removeFile" color="transparent-red" icon="trash">
        Remove
      </Button>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import { mapStores } from "pinia";
import { useAuthStore } from "@/stores";
import { PutObjectCommand, S3Client } from "@aws-sdk/client-s3";
import { v4 as uuidv4 } from "uuid";

export default defineComponent({
  name: "FileInput",
  props: {
    fieldName: String,
    description: String,
    fileUrl: String,
    fileSizeLimit: Number,
    name: String,
    id: String,
  },
  data() {
    return {
      fileData: null,
      fileError: "",
      fileName: "",
    };
  },
  computed: {
    ...mapStores(useAuthStore),
    sizeLimit() {
      if (this.fileSizeLimit) {
        return this.fileSizeLimit;
      }
      // default 5 megabytes
      return 5242880;
    },
    sizeLimitHumanReadable() {
      if (this.sizeLimit > 1000000) {
        return `${this.sizeLimit / 1000000} MB`;
      }
      return `${this.sizeLimit / 1000} KB`;
    },
    fileSrc: {
      get: function () {
        if (this.fileData) {
          return this.fileData;
        }
        if (this.fileUrl) {
          return this.fileUrl;
        }
        return null;
      },
      set: function (data) {
        this.fileData = data;
      },
    },
  },
  methods: {
    setFile(e) {
      this.fileError = "";
      const file = e.target.files[0];
      this.fileName = file.name;
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async (e) => {
        if (file.size > this.sizeLimit) {
          this.fileSrc = null;
          this.fileError = `File file size must be ${this.sizeLimitHumanReadable} or less.`;
          return;
        }
        this.fileSrc = e.target.result;
        try {
          const match = file.name.match(/\.([A-Za-z0-9]*)$/);
          if (!match) {
            return 'File is missing extension (e.g. ".gif")';
          }
          const fileExtension = match[1].toLocaleLowerCase();
          const fileName = `${uuidv4()}.${fileExtension}`;
          let contentType = "binary/octet-stream";
          if (fileExtension === "png") {
            contentType = "image/png";
          } else if (["jpeg", "jpg"].includes(fileExtension)) {
            contentType = "image/jpeg";
          } else if (fileExtension === "gif") {
            contentType = "image/gif";
          } else if (fileExtension === "ico") {
            contentType = "image/x-icon";
          } else if (fileExtension === "svg") {
            contentType = "image/svg+xml";
          }
          const authStore = useAuthStore();
          const s3 = new S3Client({
            region: import.meta.env.VITE_AWS_REGION,
            credentials: {
              accessKeyId: authStore.credentials.AccessKeyId,
              secretAccessKey: authStore.credentials.SecretKey,
              sessionToken: authStore.credentials.SessionToken,
            },
          });
          await s3.send(
            new PutObjectCommand({
              Bucket: import.meta.env.VITE_AWARD_IMAGES_BUCKET,
              Key: `public/${fileName}`,
              Body: file,
              ContentType: contentType,
            })
          );
          const url = `https://${import.meta.env.VITE_AWARD_IMAGES_BUCKET}.s3-${
            import.meta.env.VITE_AWS_REGION
          }.amazonaws.com/public/${fileName}`;
          this.$emit("file-uploaded", { url });
        } catch (error) {
          console.error(error.message);
        }
      };
    },
    removeFile() {
      this.fileError = "";
      this.fileSrc = null;
      this.fileName = "";
      this.$emit("file-removed");
    },
    downloadFile() {
      if (this.fileSrc && typeof this.fileSrc === "string") {
        const link = document.createElement("a");
        link.href = this.fileSrc;
        link.download = this.fileName || "uploaded-file";
        link.click();
      }
    },
  },
});
</script>

<style></style>
